/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactLoginMS from 'react-ms-login';

// Component imports
import { Button, Card, Col, Divider, Form, Input, Modal, Row, Spin, Typography } from 'antd';
import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { CloseCircleIcon } from '../components/Icons/CloseCircleIcon.js';
import { MicrosoftIcon } from '../components/Icons/MicrosoftIcon';
import DocumentTitle from '../components/DocumentTitle.jsx';
import GoogleLoginButton from '../components/UI/GoogleLoginButton.jsx';

// Misc imports
import { NAV_DASHBOARD, NAV_LOGIN } from '../constants/navigation';
import { AUTHENTICATION_METHODS, USER_INVITATION_STATUSES } from '../constants/main';
import { MICROSOFT_CLIENT_ID, SUPPORT_EMAIL } from '../constants/env';
import { setOrganization } from '../store/auth/authActions';
import { decodeJWT } from '../utils/auth/decodeJWT/index.js';
import { isTokenExpired } from '../utils/auth/isTokenExpired';
import { useAcceptUserInvitationMutation, useFetchUserInvitationQuery, useValidateUserInvitationMutation } from '../store/users/usersApi.js';
import { useAuthenticateUserMutation } from '../store/auth/authApi';
import replaceUrlParams from '../utils/string/replaceUrlParams/index.js';
import HelviaCard from '../components/UI/HelviaCard.jsx';
import LogoFull from '../assets/logo-full.svg';
import openConsoleAlertMessage from '../utils/misc/openConsoleAlertMessage';

// Constants declaration
const { Title } = Typography;

const UserInvitationScreen = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token: paramsToken } = useParams();
  const token = new URLSearchParams(window.location.search).get('token') || paramsToken;
  const { email, invitation_id } = decodeJWT(token);
  const { t } = useTranslation();

  const { data: userInvitation } = useFetchUserInvitationQuery({ invitationId: invitation_id }, { skip: !token });
  const [validateUserInvitation, { data: validateUserInvitationData, isLoading: isValidatingUserInvitation, isError: validateUserInvitationError }] = useValidateUserInvitationMutation({ fixedCacheKey: 'validateUserInvitation' });
  const [acceptUserInvitation, { isLoading: isAcceptingUserInvitation }] = useAcceptUserInvitationMutation();
  const [loginUser] = useAuthenticateUserMutation();

  const [userIsRegistered, setUserIsRegistered] = useState(false);
  const [acceptInvitationHasError, setAcceptInvitationHasError] = useState(false);
  const [isTokenMissing, setIsTokenMissing] = useState(false);

  const _isTokenExpired = isTokenExpired(token);
  const invitationOrganizationId = useMemo(() => decodeJWT(token)?.organization_id, [token]);

  useEffect(() => {
    if (!_isTokenExpired) {
      (async () => await validateUserInvitation({ data: { token: token } }))();
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setIsTokenMissing(true);
    }
  }, [token]);

  useEffect(() => {
    form.setFieldsValue({ email: email });
  }, [email]);

  useEffect(() => {
    (
      async () => {
        if (validateUserInvitationData?.id) {
          const res = await acceptUserInvitation({ data: { userInvitationToken: token } }).unwrap();

          if (!res) {
            setAcceptInvitationHasError(true);
            return;
          }

          setAcceptInvitationHasError(false);
          setUserIsRegistered(true);
        }
      }
    )();
  }, [validateUserInvitationData]);

  const handleSignUp = async (authenticationMethod, values) => {
    let loginValues;
    let thirdPartyEmail;
    let shouldAcceptInvitation = true;

    switch (authenticationMethod) {
      case AUTHENTICATION_METHODS.GOOGLE:
        loginValues = { loginType: AUTHENTICATION_METHODS.GOOGLE, loginToken: values.credential };
        // Check for the email
        thirdPartyEmail = decodeJWT(values.credential)?.email;
        if (thirdPartyEmail !== email) {
          openConsoleAlertMessage({ type: 'error', message: t('Email selected doesn\'t match the invitation\'s one.') });
          shouldAcceptInvitation = false;
        }
        break;
      case AUTHENTICATION_METHODS.MICROSOFT:
        loginValues = { loginType: AUTHENTICATION_METHODS.MICROSOFT, loginToken: values.access_token };
        break;
      case AUTHENTICATION_METHODS.EMAIL:
        loginValues = { password: values.password, fullName: values.fullName };
        break;
      default:
        loginValues = { password: values.password, fullName: values.fullName };
        break;
    }

    if (shouldAcceptInvitation) {
      acceptInvitation(authenticationMethod, loginValues);
    }
  };

  const acceptInvitation = async (authMethod, values) => {
    const payload = {
      userInvitationToken: token,
      ...values
    };

    const res = await acceptUserInvitation({ data: payload }).unwrap();

    if (!res) {
      setAcceptInvitationHasError(true);
      return;
    }
    setAcceptInvitationHasError(false);
    let loginValues;
    switch (authMethod) {
      case AUTHENTICATION_METHODS.GOOGLE:
        loginValues = { idToken: values.loginToken };
        break;
      case AUTHENTICATION_METHODS.MICROSOFT:
        loginValues = { accessToken: values.loginToken };
        break;
      case AUTHENTICATION_METHODS.EMAIL:
        loginValues = { email, password: values.password };
        break;
      default:
        loginValues = { email, password: values.password };
        break;
    }

    const loginResponse = await loginUser({ authenticationMethod: authMethod, data: { ...loginValues } }).unwrap();

    if (loginResponse.user) {
      const { organizations } = loginResponse.user;
      const firstAvailableOrgId = organizations[0]?.id;

      dispatch(setOrganization(firstAvailableOrgId, navigate));
      navigate(NAV_DASHBOARD);
    }
  };

  // Render form for the users that are not registered yet.
  const renderNotRegisteredFormCard = () => {
    return (
      <Form
        form={form}
        name="validation-form"
        onFinish={(values) => handleSignUp(AUTHENTICATION_METHODS.EMAIL, values)}
        initialValues={{
          fullName: '',
          email: '',
          password: ''
        }}
      >
        <Row className="text-center">
          <Col span={24}>
            <Typography.Title className="mb-2" level={2} strong>
              {t('Invitation accepted!')}
            </Typography.Title>
          </Col>
        </Row>

        <Row className="text-center mb-7">
          <Col span={24}>
            <Typography.Text className="fs-lg text-muted">
              {t('Now create your account to Helvia Console')}
            </Typography.Text>
          </Col>
        </Row>

        <Row gutter={[16, 0]} className="w-100" justify="center">
          <Col xs={24}>
            <Row gutter={[16, 8]} className="align-items-baseline">
              <Col xs={24} xxl={6}>
                {t('Full Name')}
              </Col>

              <Col xs={24} xxl={18}>
                <Form.Item
                  name="fullName"
                  rules={[{
                    required: true,
                    message: t('Please enter your full name')
                  }]}
                >
                  <Input name={['fullName']} size="large" placeholder={t('enter your full name')} prefix={<UserOutlined className="text-secondary mr-2" />} />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24}>
            <Row gutter={[16, 8]} className="align-items-baseline">
              <Col xs={24} xxl={6}>
                {t('Email')}
              </Col>

              <Col xs={24} xxl={18}>
                <Form.Item
                  name="email"
                  validateTrigger="onBlur"
                  rules={[{
                    required: true,
                    message: t('Please enter your email')
                  }, {
                    type: 'email',
                    message: t('Please enter a valid email')
                  }]}
                >
                  <Input readOnly name={['email']} size="large" placeholder={t('enter your email')} prefix={<MailOutlined className="text-secondary mr-2" />} />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24}>
            <Row gutter={[16, 8]} className="align-items-baseline">
              <Col xs={24} xxl={6}>
                {t('Password')}
              </Col>

              <Col xs={24} xxl={18}>
                <Form.Item
                  name="password"
                  validateTrigger="onBlur"
                  rules={[{
                    required: true,
                    message: t('Please enter your password')
                  },
                  {
                    min: 8,
                    message: t('Password should consist of at least 8 characters')
                  }]}
                >
                  <Input.Password
                    autoComplete="new-password"
                    name={['password']}
                    size="large"
                    placeholder={t('enter your password')}
                    prefix={<LockOutlined className="text-secondary mr-2" />}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24}>
            <Form.Item className="mb-0">
              <Button
                className="mt-1"
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isAcceptingUserInvitation}
              >
                {t('Continue')}
              </Button>
            </Form.Item>
          </Col>

          <Col>
            <Divider>or</Divider>
          </Col>

          <Col xs={24}>
            <GoogleLoginButton
              buttonContent={
                <Fragment>
                  {t('Sign in with Google')}
                </Fragment>
              }
              onSuccess={(response) => handleSignUp(AUTHENTICATION_METHODS.GOOGLE, response)} />
          </Col>

          <Col xs={24}>
            <ReactLoginMS
              clientId={MICROSOFT_CLIENT_ID}
              redirectUri={`${window.location.origin.toString()}/ms-auth.html`}
              handleLogin={(response) => handleSignUp(AUTHENTICATION_METHODS.MICROSOFT, response)}
              responseType="token"
              cssClass="ant-btn bant-btn-default ant-btn-lg ant-btn-block"
              btnContent={
                <Fragment>
                  <MicrosoftIcon className="mr-3" />
                  {t('Sign in with Microsoft')}
                </Fragment>
              }
            />
          </Col>
        </Row>
      </Form>
    );
  };

  // Render Card for the users that are already registered
  const renderUserIsRegisteredCard = () => {
    return (
      <Row justify="center">
        <Col xs={24}>
          <Typography.Title className="mb-2 mt-5" level={2} strong>
            {t('Invitation accepted!')}
          </Typography.Title>
        </Col>

        <Col xs={24}>
          <Typography.Text className="fs-lg text-muted text-center">
            {t('You can now use your credentials to access Helvia Console')}
          </Typography.Text>
        </Col>

        <Col xs={24}>
          <Button
            className="my-5"
            type="primary"
            size="large"
            block
            onClick={() => navigate(replaceUrlParams(NAV_DASHBOARD, { orgId: invitationOrganizationId }))}
          >
            {t('Proceed to Console')}
          </Button>
        </Col>
      </Row>
    );
  };

  // Render Error Card for errors happened while accepting the invitation
  const renderAcceptErrorCard = () => {
    return (
      <Card
        title={(
          <Row justify="center">
            <Col className="w-100">
              <Title level={5} className="p-0 m-0 w-100 d-flex align-items-center justify-content-center">
                {t('An error occurred')}
              </Title>
            </Col>
          </Row>
        )}
        bordered={false}
        className="h-100"
      >
        <Row justify="center">
          <Col>
            <h4>{t('An error occurred while accepting the invitation. Please contact')} <a href={`mailTo: ${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</h4>
          </Col>
        </Row>

        <Row justify="center">
          <Col xs={18}>
            <Divider>{t('or')}</Divider>
          </Col>
        </Row>

        <Row justify="center">
          <Col>
            <Button
              className="my-5"
              type="primary"
              size="large"
              block
              onClick={() => navigate(NAV_LOGIN)}
            >
              {t('Proceed to Console')}
            </Button>
          </Col>
        </Row>
      </Card>
    );
  };

  // Render Error Card for token related errors
  const renderTokenErrorCard = () => {
    return (
      <Card
        title={(
          <Row justify="center">
            <Col className="w-100">
              <Title level={5} className="p-0 m-0 w-100 d-flex align-items-center justify-content-center">
                <CloseCircleIcon className="fs-xl text-error mr-5" />

                {
                  isTokenMissing
                    ? t('Token is missing')
                    : _isTokenExpired
                      ? t('Invitation has expired')
                      : userInvitation && userInvitation.status === USER_INVITATION_STATUSES.REVOKED
                        ? t(`We couldn't log you in`)
                        : t('An error occurred')
                }
              </Title>
            </Col>
          </Row>
        )}
        bordered={false}
        className="h-100"
      >
        <Row justify="center">
          <Col>
            <h4>{
              isTokenMissing
                ? t('Token is missing from the URL.')
                : _isTokenExpired
                  ? t('Your invitation has expired.')
                  : t('Your token seems to be invalid.')
            } {t('Please contact')} <a href={`mailTo: ${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </h4>
          </Col>
        </Row>

        <Row justify="center">
          <Col xs={18}>
            <Divider>or</Divider>
          </Col>
        </Row>

        <Row justify="center">
          <Col>
            <Button
              className="my-5"
              type="primary"
              size="large"
              block
              onClick={() => navigate(NAV_LOGIN)} // TODO: This should become NAV_REGISTRATION when the registration page is re-enabled
            >
              {t('Register now!')}
            </Button>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <Fragment>
      <DocumentTitle title={t('Accept Invitation')} />
      <HelviaCard
        leftSideContent={
          <Row className="w-100 pt-10 pb-15">
            <Col md={{ span: 18, offset: 3 }}>
              {
                acceptInvitationHasError
                  ? renderAcceptErrorCard() // Invalid token - need to check status code with backend devs
                  : _isTokenExpired || (validateUserInvitationError?.error && validateUserInvitationError?.status === 400) || (userInvitation && userInvitation.status === USER_INVITATION_STATUSES.REVOKED)
                    ? renderTokenErrorCard()
                    : userIsRegistered
                      ? renderUserIsRegisteredCard()
                      : renderNotRegisteredFormCard()
              }
            </Col>
          </Row>
        }
        rightSideContent={
          <Row justify="center" align="middle" className="h-100">
            <Col>
              <img src={LogoFull} alt="logo" width={240} />
            </Col>
          </Row>
        }
      />

      <Modal title={`${t('Validating')}...`} open={isValidatingUserInvitation} footer={null} closable={false} keyboard={false} maskClosable={false}>
        <Row justify="center">
          <Col>
            <Spin />
          </Col>
        </Row>
      </Modal>
    </Fragment>
  );
};

export default UserInvitationScreen;
